/**
 * Playlist design-store catalog. Mirrors the other widget catalogs: a static
 * gallery of self-contained card SVGs the user can apply to the Playlist widget.
 * Recreated 1:1 from the Claude Design `Playlist Widgets.html` handoff (10
 * variants), cropped from the original 1920×1080 scenes (the `translate(110 290)`
 * card group → a tight card) and re-parametrised so the live playlist drives the
 * row titles + (when available) durations.
 *
 * PURE-DATA format: every design is a {@link SerializableDesign} (no JS `build()`),
 * so it can be stored/transmitted and SAFELY PUBLISHED (no code is transported).
 * The generic {@link renderDesign} turns the data into the final SVG; this module
 * only maps the live playlist rows → the design's slots/vars + list/fragment data.
 *
 * Unlike the single-value widgets, the playlist is a LIST. Each design is a
 * fixed card with up to 4 row slots; slot 0 carries the design's "active" styling
 * (progress bar / EN CURSO badge / EQ) — purely decorative, since the Playlist
 * widget has no now-playing/progress concept, it just lists the queue in order.
 * Rows beyond the 4 slots are dropped (documented cap).
 *
 * The active row (slot 0) and the queue rows (slots 1-3) have STRUCTURALLY
 * different markup, so they are modelled as keyed `fragments` (`{{FRAGMENTS:active}}`
 * + `{{FRAGMENTS:queue}}`) gated by per-row presence flags (`n0..n3`) and
 * per-row duration flags (`hd0..hd3`), rather than the uniform `list` model.
 *
 * SECURITY: row titles + durations are USER text (playlist segment names), so
 * they are bound to `text` slots (escape + cap) and XML-escaped by the renderer's
 * single choke-point before being inlined into the SVG. Every defs id is already
 * design-prefixed in the handoff (pld-/plm-/ply-/…), so the 10 cards inline into
 * one DOM document without id collisions.
 *
 * `renderPlaylistDesignSvg(design, parts)` is the single entry point the Store
 * thumbnail, the config preview and the live Pixi renderer all call.
 *
 * Known caveat: CJK accents (プレイリスト) and emoji (🌱) keep the handoff's
 * accents but fall back to a system face under the display fonts that lack those
 * glyphs — intentional, matches the mock.
 */

import { getInstalledDesign } from "../entities/designs/installedRegistry";
import { renderDesign } from "../entities/designs/renderDesign";
import type {
  DesignSlot,
  SerializableDesign,
} from "../entities/designs/serializableDesign";

export type PlaylistDesignCategory = "playlist";

/** One row the card shows: a title and an optional duration string. */
export interface PlaylistDesignRow {
  title: string;
  /** Pre-formatted duration (e.g. "12:40"); empty string hides it. */
  duration: string;
}

export interface PlaylistDesignParts {
  rows: PlaylistDesignRow[];
}

/** Representative rows for the Store thumbnails + config mock preview. */
export const SAMPLE_PLAYLIST_ROWS: PlaylistDesignRow[] = [
  { title: "saludos y café", duration: "12:40" },
  { title: "indies cooperativos", duration: "45:00" },
  { title: "charla con chat", duration: "30:00" },
  { title: "late nite · sub-only", duration: "∞" },
];

/**
 * A playlist design. Extends the shared {@link SerializableDesign} (pure data)
 * with the playlist-specific `rowSlots` metadata (the catalog still exposes the
 * same shape the Store/config/Pixi consumers read).
 */
export interface PlaylistDesign extends SerializableDesign {
  category: PlaylistDesignCategory;
  widgetType: "playlist";
  /** Number of row slots the card lays out (rows beyond this are dropped). */
  rowSlots: number;
}

/** Title slot: USER text, escaped + capped to `n` (default 30). */
const titleSlot = (n = 30): DesignSlot => ({
  kind: "text",
  escape: true,
  cap: n,
});
/** Duration slot: USER text, escaped + capped to 12. */
const durSlot: DesignSlot = { kind: "text", escape: true, cap: 12 };

/** The four title slots for a card (slot 0 cap may differ per design). */
const titleSlots = (n0 = 30, nRest = 30): Record<string, DesignSlot> => ({
  t0: titleSlot(n0),
  t1: titleSlot(nRest),
  t2: titleSlot(nRest),
  t3: titleSlot(nRest),
  d0: durSlot,
  d1: durSlot,
  d2: durSlot,
  d3: durSlot,
});

// ── 01 · Dark Minimal ──────────────────────────────────────────────────────
const darkDesign: PlaylistDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Production list",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Soft panel shadow, accent glow behind the active row, rounded progress with a glowing playhead and a count footer.",
  swatches: ["#0d0d12", "#15151d", "#a07dff", "#3ad96b"],
  cardWidth: 640,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["Space Grotesk", "Inter", "DM Mono"],
  slots: {
    count: { kind: "number", format: "plain" },
    ...titleSlots(),
  },
  frame: `<svg viewBox="0 0 640 500" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="pld-panel" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#16161e"/><stop offset="100%" stop-color="#0b0b10"/></linearGradient>
    <linearGradient id="pld-prog" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#7d5cff"/><stop offset="100%" stop-color="#b89dff"/></linearGradient>
    <filter id="pld-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="18" stdDeviation="26" flood-color="#000" flood-opacity=".55"/></filter>
    <filter id="pld-glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="16"/></filter>
  </defs>
  <rect width="640" height="500" rx="22" fill="url(#pld-panel)" stroke="#26262f" stroke-width="1.5" filter="url(#pld-shadow)"/>
  <rect x="1.5" y="1.5" width="637" height="120" rx="20" fill="#fff" opacity=".03"/>
  <rect x="34" y="40" width="4" height="24" rx="2" fill="#a07dff"/>
  <text x="50" y="59" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="25" fill="#f5f5f5" letter-spacing="0.5">Playlist de hoy</text>
  <g transform="translate(486 40)"><rect width="120" height="30" rx="15" fill="#13241a"/><circle cx="22" cy="15" r="5" fill="#3ad96b"/><circle cx="22" cy="15" r="9" fill="none" stroke="#3ad96b" stroke-width="1" opacity=".5"/><text x="38" y="20" font-family="DM Mono, monospace" font-weight="500" font-size="14" fill="#3ad96b" letter-spacing="1">EN VIVO</text></g>
  <line x1="34" y1="84" x2="606" y2="84" stroke="#23232c"/>
  {{FRAGMENTS:active}}
  <g font-family="Inter, sans-serif">
    {{FRAGMENTS:queue}}
  </g>
  <line x1="34" y1="440" x2="606" y2="440" stroke="#23232c"/>
  <text x="34" y="474" font-family="DM Mono, monospace" font-size="14" fill="#6b7280" letter-spacing="2">{{count}} SEGMENTOS</text>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<ellipse cx="180" cy="142" rx="200" ry="40" fill="#a07dff" opacity=".16" filter="url(#pld-glow)"/>
  <g transform="translate(34 100)">
    <rect width="572" height="86" rx="14" fill="#191230" stroke="#a07dff" stroke-width="1" stroke-opacity=".5"/>
    <rect x="0" y="0" width="3" height="86" rx="1.5" fill="#a07dff"/>
    <text x="30" y="40" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#b89dff">01</text>
    <circle cx="34" cy="62" r="13" fill="#a07dff"/><polygon points="30,56 30,68 41,62" fill="#150d28"/>
    <text x="68" y="40" font-family="Inter, sans-serif" font-weight="600" font-size="26" fill="#fff">{{t0}}</text>
    <g transform="translate(68 56)"><rect width="380" height="6" rx="3" fill="#2a2440"/><rect width="150" height="6" rx="3" fill="url(#pld-prog)"/><circle cx="150" cy="3" r="6" fill="#fff"/><circle cx="150" cy="3" r="10" fill="#b89dff" opacity=".4" filter="url(#pld-glow)"/></g>
    <g transform="translate(458 30)"><rect width="114" height="26" rx="13" fill="#a07dff"/><text x="57" y="18" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="13" fill="#150d28" letter-spacing="1">EN CURSO</text></g>
    `,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="552" y="74" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#9a8bc5">{{d0}}</text>`,
    },
    {
      key: "active",
      showIf: "n0",
      svg: `
  </g>`,
    },
    {
      key: "queue",
      showIf: "n1",
      svg: `<g transform="translate(34 200)"><rect width="572" height="64" rx="14" fill="#14141b"/><text x="30" y="40" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#5a5e6b">02</text><text x="64" y="40" font-weight="500" font-size="24" fill="#d6d9e2">{{t1}}</text>`,
    },
    {
      key: "queue",
      showIf: "n1 * hd1",
      svg: `<text x="552" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#6b7280">{{d1}}</text>`,
    },
    { key: "queue", showIf: "n1", svg: `</g>` },
    {
      key: "queue",
      showIf: "n2",
      svg: `<g transform="translate(34 276)"><rect width="572" height="64" rx="14" fill="#14141b"/><text x="30" y="40" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#5a5e6b">03</text><text x="64" y="40" font-weight="500" font-size="24" fill="#d6d9e2">{{t2}}</text>`,
    },
    {
      key: "queue",
      showIf: "n2 * hd2",
      svg: `<text x="552" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#6b7280">{{d2}}</text>`,
    },
    { key: "queue", showIf: "n2", svg: `</g>` },
    {
      key: "queue",
      showIf: "n3",
      svg: `<g transform="translate(34 352)"><rect width="572" height="64" rx="14" fill="#14141b"/><text x="30" y="40" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#5a5e6b">04</text><text x="64" y="40" font-weight="500" font-size="24" fill="#d6d9e2">{{t3}}</text>`,
    },
    {
      key: "queue",
      showIf: "n3 * hd3",
      svg: `<text x="552" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#6b7280">{{d3}}</text>`,
    },
    { key: "queue", showIf: "n3", svg: `</g>` },
  ],
};

/**
 * Build the three `queue` fragments for one row whose conditional duration is
 * the LAST child of the row `<g>`: open-group+content (gated on the row),
 * the duration `<text>` (gated on row+duration), then the `</g>` close.
 * Mirrors the old `[1,2,3].map(i => has(rows,i) ? row + (D ? dur : "") : "")`.
 */
const queueRow = (
  i: number,
  prefixSvg: string,
  durSvg: string,
  closeSvg = `</g>`,
): NonNullable<PlaylistDesign["fragments"]> => [
  { key: "queue", showIf: `n${i}`, svg: prefixSvg },
  { key: "queue", showIf: `n${i} * hd${i}`, svg: durSvg },
  { key: "queue", showIf: `n${i}`, svg: closeSvg },
];

// ── 02 · Music Player ──────────────────────────────────────────────────────
const musicDesign: PlaylistDesign = {
  id: "music",
  name: "Music Player",
  subtitle: "Up next queue",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Album thumbs with inner sheen, EQ on the playing row, seek bar with a glowing knob and an up-next queue.",
  swatches: ["#0c0814", "#1db954", "#ffffff", "#9a8bb5"],
  cardWidth: 640,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["Inter", "DM Mono"],
  slots: { count: { kind: "number", format: "plain" }, ...titleSlots() },
  frame: `<svg viewBox="0 0 640 500" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="plm-panel" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#140d22"/><stop offset="100%" stop-color="#0a0612"/></linearGradient>
    <linearGradient id="plm-a1" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#7d4cff"/><stop offset="100%" stop-color="#1db954"/></linearGradient>
    <linearGradient id="plm-a2" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#ff7ad0"/><stop offset="100%" stop-color="#ffb347"/></linearGradient>
    <linearGradient id="plm-a3" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#2ce5ff"/><stop offset="100%" stop-color="#1c5fa0"/></linearGradient>
    <linearGradient id="plm-a4" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#ff5a7a"/><stop offset="100%" stop-color="#7d4cff"/></linearGradient>
    <filter id="plm-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="18" stdDeviation="26" flood-color="#000" flood-opacity=".55"/></filter>
    <filter id="plm-glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="6"/></filter>
  </defs>
  <rect width="640" height="500" rx="18" fill="url(#plm-panel)" filter="url(#plm-shadow)"/>
  <rect x="1" y="1" width="638" height="90" rx="17" fill="#fff" opacity=".03"/>
  <g transform="translate(34 38)"><rect width="14" height="14" rx="3" fill="#1db954"/><text x="26" y="13" font-family="Inter, sans-serif" font-weight="800" font-size="15" fill="#1db954" letter-spacing="2">REPRODUCIENDO</text></g>
  <text x="606" y="51" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#9a8bb5">{{count}} segmentos</text>
  <line x1="34" y1="68" x2="606" y2="68" stroke="#1d1730"/>
  {{FRAGMENTS:active}}
  <text x="34" y="214" font-family="Inter, sans-serif" font-weight="700" font-size="14" fill="#6b5e85" letter-spacing="2">A CONTINUACIÓN</text>
  <g font-family="Inter, sans-serif">
    {{FRAGMENTS:queue}}
  </g>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<g transform="translate(34 86)"><rect width="572" height="96" rx="14" fill="#1b1330" stroke="#1db954" stroke-width="1" stroke-opacity=".4"/><rect x="16" y="16" width="64" height="64" rx="10" fill="url(#plm-a1)"/><rect x="16" y="16" width="64" height="22" rx="10" fill="#fff" opacity=".18"/><g transform="translate(48 70)" fill="#1db954"><rect x="-18" y="-18" width="6" height="18"/><rect x="-8" y="-28" width="6" height="28"/><rect x="2" y="-12" width="6" height="12"/><rect x="12" y="-24" width="6" height="24"/></g><text x="98" y="44" font-family="Inter, sans-serif" font-weight="700" font-size="26" fill="#fff">{{t0}}</text><g transform="translate(98 62)"><rect width="360" height="6" rx="3" fill="#2a2440"/><rect width="142" height="6" rx="3" fill="#1db954"/><circle cx="142" cy="3" r="7" fill="#fff"/><circle cx="142" cy="3" r="11" fill="#1db954" opacity=".4" filter="url(#plm-glow)"/></g>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="552" y="50" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#9a8bb5">{{d0}}</text>`,
    },
    { key: "active", showIf: "n0", svg: `</g>` },
    ...queueRow(
      1,
      `<g transform="translate(34 230)"><rect width="572" height="68" rx="12" fill="#140e22"/><rect x="14" y="12" width="44" height="44" rx="8" fill="url(#plm-a2)"/><rect x="14" y="12" width="44" height="16" rx="8" fill="#fff" opacity=".18"/><text x="74" y="42" font-weight="600" font-size="23" fill="#e8e0f5">{{t1}}</text>`,
      `<text x="552" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#9a8bb5">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(34 308)"><rect width="572" height="68" rx="12" fill="#140e22"/><rect x="14" y="12" width="44" height="44" rx="8" fill="url(#plm-a3)"/><rect x="14" y="12" width="44" height="16" rx="8" fill="#fff" opacity=".18"/><text x="74" y="42" font-weight="600" font-size="23" fill="#e8e0f5">{{t2}}</text>`,
      `<text x="552" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#9a8bb5">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(34 386)"><rect width="572" height="68" rx="12" fill="#140e22"/><rect x="14" y="12" width="44" height="44" rx="8" fill="url(#plm-a4)"/><rect x="14" y="12" width="44" height="16" rx="8" fill="#fff" opacity=".18"/><text x="74" y="42" font-weight="600" font-size="23" fill="#e8e0f5">{{t3}}</text>`,
      `<text x="552" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#9a8bb5">{{d3}}</text>`,
    ),
  ],
};

// ── 03 · Aqua Pro ──────────────────────────────────────────────────────────
const y2kDesign: PlaylistDesign = {
  id: "y2k",
  name: "Aqua Pro",
  subtitle: "Glass rows",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Refined glossy header, gel highlight on the live row, rounded progress with knob and crisp numeric chips.",
  swatches: ["#04284f", "#1c8fff", "#8acfff", "#27d17c"],
  cardWidth: 640,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["Audiowide", "Inter", "DM Mono"],
  slots: { count: { kind: "number", format: "plain" }, ...titleSlots() },
  frame: `<svg viewBox="0 0 640 500" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="ply-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#0a3f7a"/><stop offset="100%" stop-color="#04284f"/></linearGradient>
    <linearGradient id="ply-hd" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#4ab0ff"/><stop offset="50%" stop-color="#2a8fef"/><stop offset="100%" stop-color="#1c7fe0"/></linearGradient>
    <linearGradient id="ply-live" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#eafff2"/><stop offset="100%" stop-color="#bfeed2"/></linearGradient>
    <linearGradient id="ply-prog" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#27d17c"/><stop offset="100%" stop-color="#7dffb0"/></linearGradient>
    <filter id="ply-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="18" stdDeviation="26" flood-color="#001026" flood-opacity=".55"/></filter>
    <filter id="ply-glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="6"/></filter>
  </defs>
  <rect width="640" height="500" rx="26" fill="url(#ply-bg)" stroke="#fff" stroke-width="2.5" filter="url(#ply-shadow)"/>
  <rect x="12" y="12" width="616" height="56" rx="16" fill="url(#ply-hd)"/>
  <ellipse cx="320" cy="26" rx="290" ry="8" fill="#fff" opacity=".55"/>
  <text x="34" y="50" font-family="Audiowide, sans-serif" font-size="22" fill="#fff">PLAYLIST</text>
  <g transform="translate(556 40)"><rect x="-44" y="-14" width="68" height="28" rx="14" fill="#0a4a22"/><circle cx="-26" cy="0" r="5" fill="#5aff8a"/><text x="-14" y="5" font-family="DM Mono, monospace" font-size="13" fill="#caffd6">LIVE</text></g>
  <g font-family="Inter, sans-serif">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <text x="28" y="438" font-family="DM Mono, monospace" font-size="13" fill="#9fd4ff" letter-spacing="2">{{count}} SEGMENTOS</text>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<g transform="translate(28 86)"><rect width="584" height="78" rx="18" fill="url(#ply-live)" stroke="#fff" stroke-width="1.5"/><ellipse cx="292" cy="14" rx="260" ry="5" fill="#fff" opacity=".6"/><text x="22" y="46" font-family="Audiowide, sans-serif" font-size="22" fill="#0a4a22">1</text><text x="58" y="42" font-weight="700" font-size="24" fill="#063a1a">{{t0}}</text><g transform="translate(58 54)"><rect width="300" height="6" rx="3" fill="#a8d8bc"/><rect width="120" height="6" rx="3" fill="url(#ply-prog)"/><circle cx="120" cy="3" r="6" fill="#fff"/><circle cx="120" cy="3" r="10" fill="#27d17c" opacity=".5" filter="url(#ply-glow)"/></g>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="562" y="46" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#0a6a32">{{d0}}</text>`,
    },
    { key: "active", showIf: "n0", svg: `</g>` },
    ...queueRow(
      1,
      `<g transform="translate(28 174)"><rect width="584" height="64" rx="18" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.3)" stroke-width="1"/><text x="22" y="42" font-family="Audiowide, sans-serif" font-size="20" fill="#8acfff">2</text><text x="58" y="42" font-weight="600" font-size="23" fill="#eaf6ff">{{t1}}</text>`,
      `<text x="562" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#9fd4ff">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(28 248)"><rect width="584" height="64" rx="18" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.3)" stroke-width="1"/><text x="22" y="42" font-family="Audiowide, sans-serif" font-size="20" fill="#8acfff">3</text><text x="58" y="42" font-weight="600" font-size="23" fill="#eaf6ff">{{t2}}</text>`,
      `<text x="562" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#9fd4ff">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(28 322)"><rect width="584" height="64" rx="18" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.3)" stroke-width="1"/><text x="22" y="42" font-family="Audiowide, sans-serif" font-size="20" fill="#8acfff">4</text><text x="58" y="42" font-weight="600" font-size="23" fill="#eaf6ff">{{t3}}</text>`,
      `<text x="562" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#9fd4ff">{{d3}}</text>`,
    ),
  ],
};

// ── 04 · Cyberpunk HUD ─────────────────────────────────────────────────────
const cyberDesign: PlaylistDesign = {
  id: "cyber",
  name: "Cyberpunk HUD",
  subtitle: "Set list",
  category: "playlist",
  widgetType: "playlist",
  description:
    "QUEUE:// header, hex index cells, magenta glow + scan progress on the ON AIR row and an auto-sync footer.",
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  cardWidth: 640,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["DM Mono", "Orbitron", "Inter"],
  slots: {
    count: { kind: "number", format: "plain" },
    count2: { kind: "text", escape: false },
    ...titleSlots(),
  },
  frame: `<svg viewBox="0 0 640 500" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="plc-g"><feGaussianBlur stdDeviation="1.6"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <filter id="plc-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="16" stdDeviation="24" flood-color="#000" flood-opacity=".6"/></filter>
    <filter id="plc-glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="10"/></filter>
    <pattern id="plc-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#00f0ff" opacity=".06"/></pattern>
    <linearGradient id="plc-prog" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#ff2e88"/><stop offset="100%" stop-color="#ff9ad0"/></linearGradient>
  </defs>
  <polygon points="0,30 30,0 610,0 640,30 640,470 610,500 30,500 0,470" fill="#080010" stroke="#ff2e88" stroke-width="2.5" filter="url(#plc-shadow)"/>
  <g filter="url(#plc-g)">
  <polygon points="12,36 38,12 602,12 628,36 628,464 602,488 38,488 12,464" fill="none" stroke="#00f0ff" stroke-width="1.2" opacity=".6"/>
  <rect x="12" y="12" width="616" height="476" fill="url(#plc-scan)"/>
  <text x="32" y="56" font-family="DM Mono, monospace" font-size="19" fill="#f5d019" letter-spacing="5">▮ QUEUE:// プレイリスト</text>
  <text x="608" y="56" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#00f0ff">{{count2}} SEG</text>
  <line x1="32" y1="72" x2="608" y2="72" stroke="#1a2a30"/>
  <g font-family="Orbitron, sans-serif">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <text x="32" y="448" font-family="DM Mono, monospace" font-size="13" fill="#88e8d8" letter-spacing="3">// AUTO-SYNC · {{count}} SEG</text>
  </g>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<ellipse cx="200" cy="126" rx="190" ry="34" fill="#ff2e88" opacity=".18" filter="url(#plc-glow)"/>
    <g transform="translate(32 88)"><polygon points="0,12 12,0 576,0 576,76 12,76 0,64" fill="#1c0024" stroke="#ff2e88" stroke-width="1.5"/><polygon points="14,14 26,2 64,2 64,74 14,74 14,62" fill="none" stroke="#ff2e88" stroke-width="1"/><text x="40" y="48" text-anchor="middle" font-weight="900" font-size="28" fill="#ff2e88">1</text><text x="88" y="38" font-family="Inter, sans-serif" font-weight="700" font-size="24" fill="#fff">{{t0}}</text><g transform="translate(88 52)"><rect width="320" height="6" fill="#3a1228"/><rect width="128" height="6" fill="url(#plc-prog)"/><rect x="124" y="-3" width="6" height="12" fill="#fff"/></g><text x="560" y="32" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#f5d019">● ON AIR</text>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="560" y="62" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#ff7ac8">{{d0}}</text>`,
    },
    { key: "active", showIf: "n0", svg: `</g>` },
    ...queueRow(
      1,
      `<g transform="translate(32 178)"><polygon points="0,12 12,0 576,0 576,60 12,60 0,48" fill="#0a0014" stroke="#0a4a52" stroke-width="1.2"/><text x="40" y="40" text-anchor="middle" font-weight="900" font-size="24" fill="#00f0ff">2</text><text x="88" y="40" font-family="Inter, sans-serif" font-weight="600" font-size="22" fill="#bfe9f0">{{t1}}</text>`,
      `<text x="560" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#5a8a90">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(32 248)"><polygon points="0,12 12,0 576,0 576,60 12,60 0,48" fill="#0a0014" stroke="#0a4a52" stroke-width="1.2"/><text x="40" y="40" text-anchor="middle" font-weight="900" font-size="24" fill="#00f0ff">3</text><text x="88" y="40" font-family="Inter, sans-serif" font-weight="600" font-size="22" fill="#bfe9f0">{{t2}}</text>`,
      `<text x="560" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#5a8a90">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(32 318)"><polygon points="0,12 12,0 576,0 576,60 12,60 0,48" fill="#0a0014" stroke="#0a4a52" stroke-width="1.2"/><text x="40" y="40" text-anchor="middle" font-weight="900" font-size="24" fill="#00f0ff">4</text><text x="88" y="40" font-family="Inter, sans-serif" font-weight="600" font-size="22" fill="#bfe9f0">{{t3}}</text>`,
      `<text x="560" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#5a8a90">{{d3}}</text>`,
    ),
  ],
};

// ── 05 · Editorial ─────────────────────────────────────────────────────────
const editorialDesign: PlaylistDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Programme",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Paper drop-shadow, roman numerals, dotted leaders to tabular times and a gilded EN ESCENA marker on act I.",
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  cardWidth: 640,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: { count: { kind: "number", format: "plain" }, ...titleSlots() },
  frame: `<svg viewBox="0 0 640 500" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="ple-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="16" stdDeviation="22" flood-color="#000" flood-opacity=".4"/></filter></defs>
  <rect width="640" height="500" fill="#f5efe2" filter="url(#ple-shadow)"/>
  <line x1="24" y1="24" x2="616" y2="24" stroke="#1a1815" stroke-width="3"/>
  <line x1="24" y1="32" x2="616" y2="32" stroke="#1a1815" stroke-width="1"/>
  <text x="320" y="82" text-anchor="middle" font-family="Cormorant Garamond, serif" font-weight="600" font-size="42" fill="#1a1815">Programa de hoy</text>
  <text x="320" y="108" text-anchor="middle" font-family="DM Mono, monospace" font-size="12" fill="#8a3a2a" letter-spacing="8">— EN CARTEL —</text>
  <line x1="24" y1="128" x2="616" y2="128" stroke="#1a1815" stroke-width="1"/>
  <g font-family="Cormorant Garamond, serif" fill="#1a1815">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <line x1="24" y1="446" x2="616" y2="446" stroke="#1a1815" stroke-width="1"/>
  <text x="34" y="478" font-family="DM Mono, monospace" font-size="12" fill="#8a3a2a" letter-spacing="4">{{count}} ACTOS · @eriol.streams</text>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<g transform="translate(34 184)"><rect x="-10" y="-38" width="616" height="62" fill="#f0e6cf"/><text font-weight="600" font-size="30" fill="#8a3a2a">I.</text><text x="56" font-style="italic" font-weight="600" font-size="32">{{t0}}</text>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<line x1="290" y1="-8" x2="486" y2="-8" stroke="#c8a050" stroke-width="1" stroke-dasharray="2 5"/><text x="582" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#1a1815">{{d0}}</text>`,
    },
    {
      key: "active",
      showIf: "n0",
      svg: `<text x="56" y="26" font-family="DM Mono, monospace" font-size="12" fill="#8a3a2a" letter-spacing="3">▸ EN ESCENA</text></g>`,
    },
    ...queueRow(
      1,
      `<g transform="translate(34 268)"><text font-weight="600" font-size="30">II.</text><text x="64" font-style="italic" font-weight="600" font-size="32">{{t1}}</text>`,
      `<line x1="340" y1="-8" x2="486" y2="-8" stroke="#c8a050" stroke-width="1" stroke-dasharray="2 5"/><text x="582" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#1a1815">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(34 340)"><text font-weight="600" font-size="30">III.</text><text x="64" font-style="italic" font-weight="600" font-size="32">{{t2}}</text>`,
      `<line x1="340" y1="-8" x2="486" y2="-8" stroke="#c8a050" stroke-width="1" stroke-dasharray="2 5"/><text x="582" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#1a1815">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(34 412)"><text font-weight="600" font-size="30">IV.</text><text x="64" font-style="italic" font-weight="600" font-size="32">{{t3}}</text>`,
      `<line x1="340" y1="-8" x2="486" y2="-8" stroke="#c8a050" stroke-width="1" stroke-dasharray="2 5"/><text x="582" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#1a1815">{{d3}}</text>`,
    ),
  ],
};

// ── 06 · Pixel Arcade ──────────────────────────────────────────────────────
const pixelDesign: PlaylistDesign = {
  id: "pixel",
  name: "Pixel Arcade",
  subtitle: "Stage select",
  category: "playlist",
  widgetType: "playlist",
  description:
    "STAGE SELECT box with a ▶ cursor, NOW tag, blocky XP progress and tidy pixel durations.",
  swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
  cardWidth: 644,
  cardHeight: 506,
  rowSlots: 4,
  fonts: ["Press Start 2P"],
  slots: { count: { kind: "number", format: "plain" }, ...titleSlots(16, 18) },
  frame: `<svg viewBox="0 0 644 506" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
  <rect x="20" y="22" width="624" height="484" fill="#000" opacity=".4"/>
  <rect width="640" height="500" fill="#48cae4"/>
  <rect x="8" y="8" width="624" height="484" fill="#1d2a3a"/>
  <rect x="16" y="16" width="608" height="468" fill="#0a121c"/>
  <g fill="#ffb703"><rect width="24" height="24"/><rect x="616" width="24" height="24"/><rect y="468" width="24" height="24"/><rect x="616" y="468" width="24" height="24"/></g>
  <g transform="translate(40 -16)"><rect width="300" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="288" height="20" fill="#1d2a3a"/><text x="150" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703">STAGE SELECT</text></g>
  <text x="600" y="58" text-anchor="end" font-family="Press Start 2P, monospace" font-size="11" fill="#48cae4">{{count}} STG</text>
  <g font-family="Press Start 2P, monospace" fill="#fff">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <text x="40" y="446" font-family="Press Start 2P, monospace" font-size="10" fill="#48cae4">▶ AUTO · {{count}} STG</text>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<g transform="translate(40 78)"><rect width="560" height="78" fill="#102233" stroke="#ffb703" stroke-width="3"/><polygon points="16,26 16,52 38,39" fill="#ffb703"/><text x="56" y="44" font-size="16" fill="#ffb703">1</text><text x="92" y="38" font-size="13" fill="#fff">{{t0}}</text><g transform="translate(92 50)" fill="#ffb703"><rect width="14" height="12"/><rect x="18" width="14" height="12"/><rect x="36" width="14" height="12"/><rect x="54" width="14" height="12" fill="#2a3a4a"/><rect x="72" width="14" height="12" fill="#2a3a4a"/></g><text x="540" y="34" text-anchor="end" font-size="10" fill="#ffb703">NOW</text>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="540" y="56" text-anchor="end" font-size="10" fill="#7fd6e8">{{d0}}</text>`,
    },
    { key: "active", showIf: "n0", svg: `</g>` },
    ...queueRow(
      1,
      `<g transform="translate(40 168)"><rect width="560" height="64" fill="#12202c" stroke="#2a3a4a" stroke-width="2"/><text x="20" y="40" font-size="16" fill="#48cae4">2</text><text x="58" y="40" font-size="13" fill="#c8d6e0">{{t1}}</text>`,
      `<text x="540" y="40" text-anchor="end" font-size="10" fill="#5a6a78">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(40 240)"><rect width="560" height="64" fill="#12202c" stroke="#2a3a4a" stroke-width="2"/><text x="20" y="40" font-size="16" fill="#48cae4">3</text><text x="58" y="40" font-size="13" fill="#c8d6e0">{{t2}}</text>`,
      `<text x="540" y="40" text-anchor="end" font-size="10" fill="#5a6a78">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(40 312)"><rect width="560" height="64" fill="#12202c" stroke="#2a3a4a" stroke-width="2"/><text x="20" y="40" font-size="16" fill="#48cae4">4</text><text x="58" y="40" font-size="13" fill="#c8d6e0">{{t3}}</text>`,
      `<text x="540" y="40" text-anchor="end" font-size="10" fill="#5a6a78">{{d3}}</text>`,
    ),
  ],
};

// ── 07 · Cottagecore ───────────────────────────────────────────────────────
const cottageDesign: PlaylistDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Programme card",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Linen card with soft shadow, flower bullet on the live act, stitched progress thread and tabular times.",
  swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  cardWidth: 640,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: { count: { kind: "number", format: "plain" }, ...titleSlots() },
  frame: `<svg viewBox="0 0 640 500" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="plco-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="plco-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <g id="plco-fl"><circle r="9" fill="#ff9da8"/><circle cx="-9" cy="-4" r="7" fill="#ff9da8"/><circle cx="9" cy="-4" r="7" fill="#ff9da8"/><circle cx="-5" cy="7" r="7" fill="#ff9da8"/><circle cx="5" cy="7" r="7" fill="#ff9da8"/><circle r="4" fill="#ffd060"/></g>
    <filter id="plco-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="16" stdDeviation="22" flood-color="#3a2a14" flood-opacity=".4"/></filter>
  </defs>
  <rect width="640" height="500" fill="url(#plco-linen)" stroke="#6b4a2a" stroke-width="2" filter="url(#plco-shadow)"/>
  <rect width="640" height="24" fill="url(#plco-st)"/>
  <rect y="476" width="640" height="24" fill="url(#plco-st)"/>
  <rect width="24" height="500" fill="url(#plco-st)"/>
  <rect x="616" width="24" height="500" fill="url(#plco-st)"/>
  <text x="320" y="84" text-anchor="middle" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="38" fill="#2a1a08">el plan de hoy</text>
  <text x="320" y="110" text-anchor="middle" font-family="DM Mono, monospace" font-size="12" fill="#5a7a4a" letter-spacing="8">— hecho con cariño —</text>
  <line x1="48" y1="128" x2="592" y2="128" stroke="#cdb88c"/>
  <g font-family="Cormorant Garamond, serif" fill="#2a1a08">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <line x1="48" y1="440" x2="592" y2="440" stroke="#cdb88c"/>
  <text x="56" y="464" font-family="DM Mono, monospace" font-size="12" fill="#b85a4a" letter-spacing="3">{{count}} momentos · quedate 🌱</text>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<g transform="translate(56 174)"><rect x="-20" y="-32" width="582" height="64" rx="10" fill="#fbe9c8"/><use href="#plco-fl" transform="scale(1.15)"/><text x="30" y="8" font-style="italic" font-weight="600" font-size="32" fill="#b85a4a">{{t0}}</text>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="556" y="8" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#5a7a4a">{{d0}}</text>`,
    },
    {
      key: "active",
      showIf: "n0",
      svg: `<g transform="translate(30 22)"><path d="M0 0 q40 -8 80 0 q40 8 120 0" stroke="#5a7a4a" stroke-width="2" fill="none" stroke-dasharray="3 4"/></g><text x="556" y="28" text-anchor="end" font-family="DM Mono, monospace" font-size="11" fill="#5a7a4a">ahora</text></g>`,
    },
    ...queueRow(
      1,
      `<g transform="translate(56 256)"><use href="#plco-fl"/><text x="30" y="8" font-style="italic" font-weight="600" font-size="32">{{t1}}</text>`,
      `<text x="556" y="8" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#8a6a3a">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(56 326)"><use href="#plco-fl"/><text x="30" y="8" font-style="italic" font-weight="600" font-size="32">{{t2}}</text>`,
      `<text x="556" y="8" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#8a6a3a">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(56 396)"><use href="#plco-fl"/><text x="30" y="8" font-style="italic" font-weight="600" font-size="32">{{t3}}</text>`,
      `<text x="556" y="8" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#8a6a3a">{{d3}}</text>`,
    ),
  ],
};

// ── 08 · Brutalist ─────────────────────────────────────────────────────────
const brutalistDesign: PlaylistDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Index list",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Hard offset shadow, big index numerals, inverted ON AIR row with a block progress and a runtime footer.",
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  cardWidth: 656,
  cardHeight: 516,
  rowSlots: 4,
  fonts: ["Anton", "Space Grotesk", "DM Mono"],
  slots: {
    count: { kind: "number", format: "plain" },
    count2: { kind: "text", escape: false },
    ...titleSlots(),
  },
  frame: `<svg viewBox="0 0 656 516" xmlns="http://www.w3.org/2000/svg">
  <rect x="16" y="16" width="640" height="500" fill="#ff5a2a"/>
  <rect width="640" height="500" fill="#1a1a1a"/>
  <rect width="24" height="500" fill="#ff5a2a"/>
  <text x="48" y="60" font-family="DM Mono, monospace" font-size="16" fill="#ff5a2a" letter-spacing="10">// PLAYLIST.IDX</text>
  <text x="592" y="60" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#7a7a7a">{{count2}} SEG</text>
  <line x1="48" y1="80" x2="592" y2="80" stroke="#3a3a3a"/>
  <g font-family="Anton, sans-serif">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <text x="48" y="468" font-family="DM Mono, monospace" font-size="13" fill="#ff5a2a" letter-spacing="4">AUTO · {{count}} SEG</text>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<g transform="translate(48 98)"><rect width="544" height="86" fill="#ff5a2a"/><text x="18" y="64" font-size="58" fill="#1a1a1a">01</text><text x="118" y="48" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="26" fill="#1a1a1a">{{t0}}</text><g transform="translate(118 60)"><rect width="320" height="8" fill="#a83612"/><rect width="128" height="8" fill="#1a1a1a"/></g><text x="524" y="36" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#1a1a1a">ON AIR</text>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="524" y="68" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#5a1e08">{{d0}}</text>`,
    },
    { key: "active", showIf: "n0", svg: `</g>` },
    ...queueRow(
      1,
      `<g transform="translate(48 196)"><text x="18" y="56" font-size="54" fill="#3a3a3a">02</text><text x="114" y="42" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="25" fill="#f5f5f5">{{t1}}</text>`,
      `<text x="544" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#7a7a7a">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(48 268)"><text x="18" y="56" font-size="54" fill="#3a3a3a">03</text><text x="114" y="42" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="25" fill="#f5f5f5">{{t2}}</text>`,
      `<text x="544" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#7a7a7a">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(48 340)"><text x="18" y="56" font-size="54" fill="#3a3a3a">04</text><text x="114" y="42" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="25" fill="#f5f5f5">{{t3}}</text>`,
      `<text x="544" y="42" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#7a7a7a">{{d3}}</text>`,
    ),
  ],
};

// ── 09 · Anime VHS ─────────────────────────────────────────────────────────
const animeDesign: PlaylistDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "プレイリスト",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Toned RGB-split panel, glow on the ON AIR row, scan progress with knob and tidy numeric durations.",
  swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  cardWidth: 646,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["DM Mono", "VT323"],
  slots: {
    count: { kind: "number", format: "plain" },
    count2: { kind: "text", escape: false },
    ...titleSlots(),
  },
  frame: `<svg viewBox="0 0 646 500" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="pla-rgb"><feOffset in="SourceGraphic" dx="-2" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="2" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <filter id="pla-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="16" stdDeviation="24" flood-color="#000" flood-opacity=".55"/></filter>
    <filter id="pla-glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="9"/></filter>
    <pattern id="pla-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".1"/></pattern>
    <linearGradient id="pla-prog" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#ff7ad0"/><stop offset="100%" stop-color="#7ad0ff"/></linearGradient>
  </defs>
  <rect width="640" height="500" fill="#0a0218" filter="url(#pla-shadow)"/>
  <g filter="url(#pla-rgb)">
  <rect x="6" y="0" width="640" height="500" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".5"/>
  <rect width="640" height="500" fill="none" stroke="#ff7ad0" stroke-width="4"/>
  <rect x="10" y="10" width="620" height="480" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".5"/>
  <rect width="640" height="500" fill="url(#pla-scan)"/>
  <g><rect width="290" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="3">プレイリスト · LIST</text></g>
  <text x="626" y="28" text-anchor="end" font-family="VT323, monospace" font-size="24" fill="#7ad0ff">{{count2}}</text>
  <g font-family="VT323, monospace">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <text x="28" y="446" font-family="VT323, monospace" font-size="24" fill="#7ad0ff" letter-spacing="3">▮ AUTO · {{count}} · @eriol</text>
  </g>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<ellipse cx="300" cy="126" rx="240" ry="30" fill="#ff7ad0" opacity=".16" filter="url(#pla-glow)"/>
    <g transform="translate(28 88)"><rect width="584" height="76" fill="#1c0a30" stroke="#ff7ad0" stroke-width="2"/><text x="20" y="50" font-size="36" fill="#ff7ad0">1</text><text x="62" y="42" font-size="34" fill="#fff7c2">{{t0}}</text><g transform="translate(62 54)"><rect width="320" height="6" fill="#3a1a3a"/><rect width="128" height="6" fill="url(#pla-prog)"/><circle cx="128" cy="3" r="6" fill="#fff"/></g><text x="566" y="34" text-anchor="end" font-size="22" fill="#7ad0ff">▶ ON AIR</text>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="566" y="62" text-anchor="end" font-size="20" fill="#cfa8e8">{{d0}}</text>`,
    },
    { key: "active", showIf: "n0", svg: `</g>` },
    ...queueRow(
      1,
      `<g transform="translate(28 174)"><rect width="584" height="60" fill="#120420" stroke="#2a1a3a" stroke-width="2"/><text x="20" y="42" font-size="34" fill="#7ad0ff">2</text><text x="62" y="42" font-size="32" fill="#cfa8e8">{{t1}}</text>`,
      `<text x="566" y="42" text-anchor="end" font-size="22" fill="#6a5a8a">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(28 244)"><rect width="584" height="60" fill="#120420" stroke="#2a1a3a" stroke-width="2"/><text x="20" y="42" font-size="34" fill="#7ad0ff">3</text><text x="62" y="42" font-size="32" fill="#cfa8e8">{{t2}}</text>`,
      `<text x="566" y="42" text-anchor="end" font-size="22" fill="#6a5a8a">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(28 314)"><rect width="584" height="60" fill="#120420" stroke="#2a1a3a" stroke-width="2"/><text x="20" y="42" font-size="34" fill="#7ad0ff">4</text><text x="62" y="42" font-size="32" fill="#cfa8e8">{{t3}}</text>`,
      `<text x="566" y="42" text-anchor="end" font-size="22" fill="#6a5a8a">{{d3}}</text>`,
    ),
  ],
};

// ── 10 · Liquid Glass ──────────────────────────────────────────────────────
const liquidDesign: PlaylistDesign = {
  id: "liquid",
  name: "Liquid Glass",
  subtitle: "Frosted rows",
  category: "playlist",
  widgetType: "playlist",
  description:
    "Panel shadow + bokeh, glass index chips, glowing active row with seek knob and a refined runtime footer.",
  swatches: ["#0a1230", "#1c3a7a", "#9ad4ff", "#ffffff"],
  cardWidth: 640,
  cardHeight: 500,
  rowSlots: 4,
  fonts: ["Space Grotesk", "Inter", "DM Mono"],
  slots: { count: { kind: "number", format: "plain" }, ...titleSlots() },
  frame: `<svg viewBox="0 0 640 500" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="pll-bg" cx="40%" cy="15%" r="100%"><stop offset="0%" stop-color="#2a4a9a"/><stop offset="60%" stop-color="#0a1230"/><stop offset="100%" stop-color="#020615"/></radialGradient>
    <radialGradient id="pll-bok" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#9ad4ff" stop-opacity=".55"/><stop offset="100%" stop-color="#9ad4ff" stop-opacity="0"/></radialGradient>
    <filter id="pll-bl"><feGaussianBlur stdDeviation="22"/></filter>
    <filter id="pll-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="18" stdDeviation="26" flood-color="#000" flood-opacity=".55"/></filter>
    <filter id="pll-glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="7"/></filter>
    <linearGradient id="pll-prog" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#5aaaff"/><stop offset="100%" stop-color="#cfeeff"/></linearGradient>
  </defs>
  <rect width="640" height="500" rx="26" fill="url(#pll-bg)" stroke="rgba(255,255,255,.22)" stroke-width="1.5" filter="url(#pll-shadow)"/>
  <g filter="url(#pll-bl)" opacity=".55"><circle cx="140" cy="120" r="120" fill="url(#pll-bok)"/><circle cx="520" cy="420" r="150" fill="url(#pll-bok)"/></g>
  <rect x="1.5" y="1.5" width="637" height="110" rx="24" fill="#fff" opacity=".05"/>
  <text x="34" y="60" font-family="Space Grotesk, sans-serif" font-weight="600" font-size="24" fill="#fff" letter-spacing="1">Playlist</text>
  <g transform="translate(508 42)"><rect width="98" height="28" rx="14" fill="rgba(154,212,255,.18)" stroke="rgba(154,212,255,.5)" stroke-width="1"/><circle cx="20" cy="14" r="5" fill="#9ad4ff"/><text x="34" y="19" font-family="DM Mono, monospace" font-size="13" fill="#cfeeff">EN VIVO</text></g>
  <line x1="34" y1="80" x2="606" y2="80" stroke="rgba(255,255,255,.12)"/>
  <g font-family="Inter, sans-serif">
    {{FRAGMENTS:active}}
    {{FRAGMENTS:queue}}
  </g>
  <text x="34" y="446" font-family="DM Mono, monospace" font-size="13" fill="#7fa8d8" letter-spacing="2">{{count}} SEGMENTOS</text>
</svg>`,
  fragments: [
    {
      key: "active",
      showIf: "n0",
      svg: `<ellipse cx="180" cy="142" rx="200" ry="40" fill="#9ad4ff" opacity=".2" filter="url(#pll-glow)"/>
    <g transform="translate(34 98)"><rect width="572" height="84" rx="18" fill="rgba(150,210,255,.22)" stroke="rgba(190,235,255,.6)" stroke-width="1"/><rect x="16" y="22" width="44" height="44" rx="12" fill="rgba(255,255,255,.22)"/><text x="38" y="51" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="22" fill="#fff">1</text><text x="76" y="44" font-weight="700" font-size="25" fill="#fff">{{t0}}</text><g transform="translate(76 58)"><rect width="340" height="6" rx="3" fill="rgba(255,255,255,.2)"/><rect width="136" height="6" rx="3" fill="url(#pll-prog)"/><circle cx="136" cy="3" r="6" fill="#fff"/><circle cx="136" cy="3" r="10" fill="#cfeeff" opacity=".5" filter="url(#pll-glow)"/></g><text x="552" y="38" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#9ad4ff">EN CURSO</text>`,
    },
    {
      key: "active",
      showIf: "n0 * hd0",
      svg: `<text x="552" y="66" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#cfeeff">{{d0}}</text>`,
    },
    { key: "active", showIf: "n0", svg: `</g>` },
    ...queueRow(
      1,
      `<g transform="translate(34 196)"><rect width="572" height="64" rx="16" fill="rgba(255,255,255,.07)" stroke="rgba(255,255,255,.2)" stroke-width="1"/><rect x="16" y="14" width="36" height="36" rx="10" fill="rgba(255,255,255,.12)"/><text x="34" y="38" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#bfe3ff">2</text><text x="68" y="40" font-weight="500" font-size="23" fill="#eaf4ff">{{t1}}</text>`,
      `<text x="552" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#7fa8d8">{{d1}}</text>`,
    ),
    ...queueRow(
      2,
      `<g transform="translate(34 268)"><rect width="572" height="64" rx="16" fill="rgba(255,255,255,.07)" stroke="rgba(255,255,255,.2)" stroke-width="1"/><rect x="16" y="14" width="36" height="36" rx="10" fill="rgba(255,255,255,.12)"/><text x="34" y="38" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#bfe3ff">3</text><text x="68" y="40" font-weight="500" font-size="23" fill="#eaf4ff">{{t2}}</text>`,
      `<text x="552" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#7fa8d8">{{d2}}</text>`,
    ),
    ...queueRow(
      3,
      `<g transform="translate(34 340)"><rect width="572" height="64" rx="16" fill="rgba(255,255,255,.07)" stroke="rgba(255,255,255,.2)" stroke-width="1"/><rect x="16" y="14" width="36" height="36" rx="10" fill="rgba(255,255,255,.12)"/><text x="34" y="38" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="20" fill="#bfe3ff">4</text><text x="68" y="40" font-weight="500" font-size="23" fill="#eaf4ff">{{t3}}</text>`,
      `<text x="552" y="40" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#7fa8d8">{{d3}}</text>`,
    ),
  ],
};

export const PLAYLIST_DESIGNS: PlaylistDesign[] = [
  darkDesign,
  musicDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  liquidDesign,
];

/** Spanish-mock zero-padded 2-digit count ("0" → "00", "4" → "04"). */
const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Render a design's card SVG for the given rows (capped to the design's slots).
 * Maps the live rows → the design's slots/vars: per-row title (`t0..t3`),
 * duration (`d0..d3`), presence flags (`n0..n3`) and duration-present flags
 * (`hd0..hd3`), plus the segment count (`count` / zero-padded `count2`). The
 * generic {@link renderDesign} does the escaping, gating and splicing.
 */
export function renderPlaylistDesignSvg(
  design: PlaylistDesign,
  parts: PlaylistDesignParts,
): string {
  const rows = parts.rows.slice(0, design.rowSlots);
  const values: Record<string, string | number> = {
    count: rows.length,
    count2: pad2(rows.length),
  };
  // Títulos/duraciones vienen de los medios del usuario: se escapan aunque un
  // diseño de la comunidad declare `escape:false` en el slot.
  const untrusted: string[] = [];
  for (let i = 0; i < design.rowSlots; i++) {
    const row = rows[i];
    values[`n${i}`] = row ? 1 : 0;
    values[`t${i}`] = row?.title ?? "";
    values[`d${i}`] = row?.duration ?? "";
    values[`hd${i}`] = row && row.duration ? 1 : 0;
    untrusted.push(`t${i}`, `d${i}`);
  }
  return renderDesign(design, { values, untrusted });
}

export function getPlaylistDesign(
  id: string | undefined,
): PlaylistDesign | undefined {
  if (!id) {
    return undefined;
  }
  // First-party catalog wins; this is the offline, always-available set.
  const local = PLAYLIST_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Fall back to an INSTALLED community playlist design (hydrated into the
  // registry by useDesignLibrary). It is a SerializableDesign of widgetType
  // "playlist"; the playlist catalog shape (rowSlots + slots/fragments) is
  // carried in its payload, so it renders via the same renderPlaylistDesignSvg
  // path. The cast is safe: getInstalledDesign already constrains widgetType.
  const installed = getInstalledDesign(id, "playlist");
  return installed as PlaylistDesign | undefined;
}
